import { useMemo, useState } from "react"
import { SisetuCommentResult, useEditSisetuComment } from "interface/sisetu/comment"
import { Box, Button, Card, IconButton, Stack, Typography } from "@mui/material"
import { TrushIcon, EditIcon, OkIcon, CloseIcon } from "shared/icons"
import { MarkdownPreview } from "components/controls/markDownPreview"
import { Loading } from "components/controls/loading"
import { MarkdownInput } from "components/controls/markDownInput"
import { safeParseDate, zeroPadding } from "shared/util"
import { UserAvatar } from "features/user/components/avatar"

type SisetuCommentRecordProps = {
  sisetuCode: string
  isGeneral: boolean
  isSmallMedia: boolean
  isEdit: boolean
  userId: number
  comment: SisetuCommentResult
  onEdit: (comment: SisetuCommentResult) => void
  onEditCancel: () => void
  onEditted: () => void
  onRemove: (comment: SisetuCommentResult) => void
}
export const SisetuCommentRecord = ({
  sisetuCode,
  isGeneral,
  isEdit,
  userId,
  comment,
  onEdit,
  onEditCancel,
  onEditted,
  onRemove
}: SisetuCommentRecordProps) => {
  const [editData, setEditData] = useState<string>('')
  const [editState, editComment] = useEditSisetuComment(sisetuCode, {
    onEditted
  })
  const timeStamp = useMemo(
    () => {
      const time = safeParseDate(comment.updateTime)
      if (!time) return ''
      return `${zeroPadding(time.getHours(), 2)}:${zeroPadding(time.getMinutes(), 2)}`
    },
    [comment.updateTime]
  )
  return isEdit ? (
    <Stack direction="column" spacing={1}>
      {editState.posting && <Loading/>}
      <MarkdownInput
        size="small"
        value={editData}
        onChange={e => setEditData(e.target.value)}
        rows={5}
      />
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          color="error"
          onClick={() => onRemove(comment)}
          startIcon={<TrushIcon/>}
        >
          削除
        </Button>
        <Box flexGrow={1}/>
        <Button
          variant="contained"
          size="small"
          loading={editState.posting}
          onClick={() => {
            if (!editData) return
            editComment({
              commentId: comment.commentId,
              form: {
                sisetuComment: editData,
                updateTime: comment.updateTime
              }
            })
          }}
          startIcon={<OkIcon/>}
        >
          保存
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={onEditCancel}
          startIcon={<CloseIcon/>}
        >
          キャンセル
        </Button>
      </Stack>
    </Stack>
  ) : (
    <Stack direction="row">
      {comment.updateUserId === userId ? (
        <Stack direction="column" flexShrink={0} padding={1} flexGrow={1} alignItems="flex-end">
          <Typography
            variant="caption"
            lineHeight={1.2}
            color={(comment.updateTime !== comment.createTime) ? "error" : "textDisabled"}
          >
            {timeStamp}
          </Typography>
        </Stack>
      ) : comment.updateUserId ? (
        <Box paddingRight={1} paddingTop={0.5}>
          <UserAvatar
            userId={comment.updateUserId}
            userName={comment.updateUserName ?? undefined}
            sx={{ height: '1.8rem', width: '1.8rem' }}
          />
        </Box>
      ) : <></>}

      <Box>
        {(comment.updateUserId !== userId && comment.updateUserName) && (
          <Typography component="div" variant="caption">{comment.updateUserName}</Typography>
        )}
        <Card
          variant="elevation"
          sx={{
            paddingX: 1.5, paddingY: 1,
            bgcolor: comment.updateUserId === userId ? "lightgreen" : undefined,
            borderRadius: "0.5rem"
          }}
        >
          <MarkdownPreview
            value={comment.sisetuComment ?? ''}
            flexGrow={1}
            padding={0}
          />
        </Card>
      </Box>

      {(comment.updateUserId === userId && isGeneral) && (
        <Box paddingLeft={0.5}>
          <IconButton
            size="small"
            onClick={() => {
              setEditData(comment.sisetuComment)
              onEdit(comment)
            }}
          >
            <EditIcon fontSize="inherit"/>
          </IconButton>
        </Box>
      )}

      {comment.updateUserId !== userId && (
        <Stack direction="column" flexShrink={0} paddingX={1} paddingY={0}>
          <Box flexGrow={1}/>
          <Typography variant="caption" color={(comment.updateTime !== comment.createTime) ? "error" : "textDisabled"}>
            {timeStamp}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

